import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadingContainer } from './ui/Spinner';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatCurrency, formatHours } from '../utils/formatting';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

function StatisticsSummary({ className = '', refreshTrigger = 0 }) {
    const [timeframe, setTimeframe] = useState('ALL_TIME');
    const [location, setLocation] = useState('ALL');
    const [stake, setStake] = useState('ALL');
    const [includeRakeback, setIncludeRakeback] = useState(false);
    const [loading, setLoading] = useState(true);
    const [locations, setLocations] = useState([]);
    const [stakes, setStakes] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState({
        win_rate: 0,
        total_profit: 0,
        hourly_rate: 0,
        total_hours: 0,
        total_sessions: 0,
        avg_session_length: 0,
        ytd_profit: 0,
        mtd_profit: 0,
        wtd_profit: 0,
        // Expanded stats
        day_of_week_stats: [],
        best_session: null,
        worst_session: null,
        streak: null,
        avg_session_profit: 0,
        avg_session_hours: 0,
        avg_buy_in: 0,
        profit_std_dev: 0,
        location_stats: []
    });
    const [error, setError] = useState(null);

    // Fetch locations and stakes
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [locationsResponse, stakesResponse] = await Promise.all([
                    axios.get('/api/locations/'),
                    axios.get('/api/stakes/')
                ]);
                setLocations(locationsResponse.data || []);
                setStakes(stakesResponse.data || []);
            } catch (err) {
                console.error('Failed to fetch filter data:', err);
                setLocations([]);
                setStakes([]);
            }
        };
        fetchData();
    }, []);

    // Fetch statistics and sessions
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const statsParams = {
                    timeframe,
                    location_id: location === 'ALL' ? undefined : location,
                    stake_id: stake === 'ALL' ? undefined : stake,
                    includeRakeback: includeRakeback
                };

                const sessionsParams = {
                    timeframe,
                    location_id: location === 'ALL' ? undefined : location,
                    stake_id: stake === 'ALL' ? undefined : stake
                };

                const [statsResponse, sessionsResponse] = await Promise.all([
                    axios.get('/api/statistics-overview/', { params: statsParams }),
                    axios.get('/api/sessions/', { 
                        params: { 
                            ...sessionsParams,
                            ordering: 'start_time'  // Order by start time ascending
                        } 
                    })
                ]);

                // Handle the response and ensure we have all needed fields
                // Handle both paginated response (with results) and direct array
                const rawSessions = Array.isArray(sessionsResponse.data)
                    ? sessionsResponse.data
                    : (sessionsResponse.data.results || []);

                const sessionsData = rawSessions.map(session => ({
                    ...session,
                    cash_out: parseFloat(session.cash_out || 0),
                    buy_in: parseFloat(session.buy_in || 0),
                    rakeback: parseFloat(session.rakeback || 0)
                }));

                console.log('SESSIONS DATA RECEIVED BY FRONTEND:', sessionsData);

                setStats({
                    win_rate: statsResponse.data.win_rate || 0,
                    total_profit: statsResponse.data.current_total_profit || 0,
                    hourly_rate: statsResponse.data.profit_per_hour || 0,
                    total_hours: statsResponse.data.total_duration_hours_in_period || 0,
                    total_sessions: statsResponse.data.total_sessions_in_period || 0,
                    avg_session_length: statsResponse.data.total_duration_hours_in_period || 0,
                    // Expanded stats
                    day_of_week_stats: statsResponse.data.day_of_week_stats || [],
                    best_session: statsResponse.data.best_session || null,
                    worst_session: statsResponse.data.worst_session || null,
                    streak: statsResponse.data.streak || null,
                    avg_session_profit: statsResponse.data.avg_session_profit || 0,
                    avg_session_hours: statsResponse.data.avg_session_hours || 0,
                    avg_buy_in: statsResponse.data.avg_buy_in || 0,
                    profit_std_dev: statsResponse.data.profit_std_dev || 0,
                    location_stats: statsResponse.data.location_stats || []
                });

                // Process sessions for the graph
                const processedSessions = sessionsData.map(session => {
                    // Validate and format the date properly
                    let formattedDate = 'Unknown Date';
                    if (session.start_time) {
                        const dateObj = new Date(session.start_time);
                        if (!isNaN(dateObj.getTime())) {
                            formattedDate = dateObj.toLocaleDateString();
                        }
                    }
                    
                    // Calculate profit as cash_out - buy_in
                    const sessionProfit = parseFloat(session.cash_out || 0) - parseFloat(session.buy_in || 0);
                    
                    return {
                        date: formattedDate,
                        dateObj: session.start_time ? new Date(session.start_time) : null,
                        profit: sessionProfit,
                        rawProfit: session.profit,
                        rakeback: session.rakeback || 0,
                        cumulativeProfit: 0
                    };
                }).filter(session => session.date !== 'Unknown Date' && session.dateObj); // Filter out sessions with invalid dates

                // Sort sessions by date in ascending order
                processedSessions.sort((a, b) => a.dateObj - b.dateObj);

                // Add starting point at (0,0) before any sessions
                const sessionsWithStart = [];
                let runningTotal = 0;
                
                // Add initial point at (0,0)
                const firstSessionDate = processedSessions[0]?.dateObj;
                if (firstSessionDate) {
                    const dayBefore = new Date(firstSessionDate);
                    dayBefore.setDate(dayBefore.getDate() - 1);
                    sessionsWithStart.push({
                        date: dayBefore.toLocaleDateString(),
                        profit: 0,
                        cumulativeProfit: 0
                    });
                } else {
                    sessionsWithStart.push({
                        date: 'Start',
                        profit: 0,
                        cumulativeProfit: 0
                    });
                }
                console.log('Starting cumulative profit calculation:');
                console.log('Initial total profit from API:', statsResponse.data.current_total_profit);
                
                processedSessions.forEach((session, index) => {
                    const previousTotal = runningTotal;
                    const sessionRakeback = parseFloat(session.rakeback) || 0;
                    
                    // Calculate session profit based on rakeback inclusion
                    const sessionTotal = includeRakeback ? 
                        session.profit + sessionRakeback : 
                        session.profit;
                    
                    // Log detailed session info
                    console.log('Processing session:', {
                        date: session.date,
                        cash_out: session.cash_out,
                        buy_in: session.buy_in,
                        profit: session.profit,
                        rakeback: sessionRakeback,
                        total: sessionTotal
                    });
                    
                    // Update running total
                    runningTotal += sessionTotal;
                    
                    console.log(`Session ${index + 1}:`, {
                        date: session.date,
                        baseProfit: session.profit,
                        rakeback: sessionRakeback,
                        sessionTotal: sessionTotal,
                        previousTotal: previousTotal,
                        newTotal: runningTotal,
                        includeRakeback: includeRakeback
                    });
                    
                    sessionsWithStart.push({
                        date: session.date,
                        profit: session.profit,
                        rakeback: sessionRakeback,
                        cumulativeProfit: runningTotal
                    });
                });

                // Get the final cumulative profit from the graph
                const finalCumulativeProfit = sessionsWithStart[sessionsWithStart.length - 1]?.cumulativeProfit || 0;
                const totalProfit = statsResponse.data.current_total_profit || 0;
                
                // Allow for a small floating point difference (less than $1)
                if (Math.abs(finalCumulativeProfit - totalProfit) >= 1) {
                    console.warn('Profit mismatch detected:', {
                        graphTotal: finalCumulativeProfit,
                        summaryTotal: totalProfit,
                        difference: finalCumulativeProfit - totalProfit,
                        includeRakeback: includeRakeback
                    });
                }

                setSessions(sessionsWithStart);
            } catch (err) {
                setError('Failed to fetch statistics.');
                console.error('Error fetching statistics:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [timeframe, location, stake, includeRakeback, refreshTrigger]);

    if (error) {
        return (
            <div className={`bg-beige-50 border border-beige-200 dark:border-gray-700 
                         rounded-lg p-4 shadow-sm h-full flex flex-col 
                         min-h-[300px] ${className}`}>
                <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Statistics</h2>
                </div>
                <div className="flex-grow flex items-center justify-center">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            </div>
        );
    }

    const chartData = {
        labels: sessions.length > 0 ? sessions.map(session => session.date) : ['No Data'],
        datasets: [
            {
                label: 'Cumulative PnL',
                data: sessions.length > 0 ? sessions.map(session => session.cumulativeProfit) : [0],
                borderColor: '#8B4513',
                tension: 0,  // Changed from 0.4 to 0 for straight lines
                pointRadius: sessions.length > 0 ? 2 : 4,  // Show small points for data points
                pointHoverRadius: 6,  // Larger points on hover
                borderWidth: 2,
                fill: false,
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false  // Hide legend since we only have one line
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            }
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45
                },
                grid: {
                    display: false  // Hide vertical grid lines
                }
            },
            y: {
                beginAtZero: true,  // Start Y-axis from 0
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'  // Lighter grid lines
                },
                ticks: {
                    callback: function(value) {
                        return '$' + value.toLocaleString();
                    }
                },
                // Ensure proper scaling for negative values
                afterDataLimits: function(axis) {
                    // If all values are negative, ensure 0 is included
                    if (axis.min < 0 && axis.max < 0) {
                        axis.max = 0;
                    }
                    // If all values are positive, ensure 0 is included
                    if (axis.min > 0 && axis.max > 0) {
                        axis.min = 0;
                    }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        elements: {
            line: {
                tension: 0  // Also set to 0 here for consistency
            }
        }
    };

    return (
        <div className={`${className} h-full flex flex-col min-h-[400px] sm:min-h-[500px]`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Statistics</h2>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <select
                            value={timeframe}
                            onChange={(e) => setTimeframe(e.target.value)}
                            className="text-xs sm:text-sm rounded-lg border border-gray-200 
                                     bg-white dark:bg-gray-700 px-2 py-1 sm:px-3 sm:py-2
                                     text-gray-900 dark:text-white focus:ring-1 
                                     focus:ring-primary-500 dark:focus:ring-primary-400
                                     min-w-[120px]"
                        >
                            <option value="ALL_TIME">All Time</option>
                            <option value="YTD">Year to Date</option>
                            <option value="MTD">Month to Date</option>
                            <option value="WTD">Week to Date</option>
                            <option value="LAST_7_DAYS">Last 7 Days</option>
                            <option value="LAST_30_DAYS">Last 30 Days</option>
                            <option value="LAST_3_MONTHS">Last 3 Months</option>
                            <option value="LAST_6_MONTHS">Last 6 Months</option>
                            <option value="LAST_12_MONTHS">Last 12 Months</option>
                            <option value="LAST_YEAR">Last Year</option>
                            <option value="THIS_MONTH">This Month</option>
                            <option value="THIS_WEEK">This Week</option>
                        </select>
                        <select
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="text-xs sm:text-sm rounded-lg border border-gray-200 
                                     bg-white dark:bg-gray-700 px-2 py-1 sm:px-3 sm:py-2
                                     text-gray-900 dark:text-white focus:ring-1 
                                     focus:ring-primary-500 dark:focus:ring-primary-400
                                     min-w-[120px]"
                        >
                            <option value="ALL">All Locations</option>
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                        <select
                            value={stake}
                            onChange={(e) => setStake(e.target.value)}
                            className="text-xs sm:text-sm rounded-lg border border-gray-200 
                                     bg-white dark:bg-gray-700 px-2 py-1 sm:px-3 sm:py-2
                                     text-gray-900 dark:text-white focus:ring-1 
                                     focus:ring-primary-500 dark:focus:ring-primary-400
                                     min-w-[120px]"
                        >
                            <option value="ALL">All Stakes</option>
                            {stakes.map(stk => (
                                <option key={stk.id} value={stk.id}>{stk.name}</option>
                            ))}
                        </select>
                        <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={includeRakeback}
                                    onChange={(e) => setIncludeRakeback(e.target.checked)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                Include Rakeback
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <LoadingContainer />
            ) : (
                <div className="flex-grow p-4 sm:p-6 pt-0 sm:pt-0">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                        <div className="text-center">
                            <div className={`text-base sm:text-lg font-bold ${stats.total_profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {formatCurrency(stats.total_profit)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total Profit</div>
                        </div>
                        <div className="text-center">
                            <div className={`text-base sm:text-lg font-bold ${stats.win_rate >= 50 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {stats.win_rate}%
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Win Rate</div>
                        </div>
                        <div className="text-center">
                            <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                                {stats.total_sessions}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sessions</div>
                        </div>
                        <div className="text-center">
                            <div className={`text-base sm:text-lg font-bold ${stats.hourly_rate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {formatCurrency(stats.hourly_rate)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Hourly Rate</div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                        <div className="text-center">
                            <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                                {formatHours(stats.total_hours)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Hours Played</div>
                        </div>
                        <div className="text-center">
                            <div className={`text-base sm:text-lg font-bold ${stats.avg_session_profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {formatCurrency(stats.avg_session_profit)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Avg Session</div>
                        </div>
                        <div className="text-center">
                            <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                                {stats.avg_session_hours}h
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Avg Duration</div>
                        </div>
                        <div className="text-center">
                            <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(stats.avg_buy_in)}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Avg Buy-in</div>
                        </div>
                    </div>

                    {/* Streak & Best/Worst */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                        {stats.streak && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Streak</div>
                                <div className={`text-lg font-bold ${stats.streak.current_streak_type === 'win' ? 'text-green-600 dark:text-green-400' : stats.streak.current_streak_type === 'loss' ? 'text-red-600 dark:text-red-400' : 'text-gray-600'}`}>
                                    {stats.streak.current_streak > 0 ? `${stats.streak.current_streak} ${stats.streak.current_streak_type === 'win' ? 'W' : 'L'}` : '-'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Best: {stats.streak.longest_win_streak}W | Worst: {stats.streak.longest_loss_streak}L
                                </div>
                            </div>
                        )}
                        {stats.best_session && (
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Best Session</div>
                                <div className="text-lg font-bold text-green-600 dark:text-green-400">
                                    {formatCurrency(stats.best_session.profit)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {stats.best_session.date} @ {stats.best_session.location}
                                </div>
                            </div>
                        )}
                        {stats.worst_session && (
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Worst Session</div>
                                <div className="text-lg font-bold text-red-600 dark:text-red-400">
                                    {formatCurrency(stats.worst_session.profit)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {stats.worst_session.date} @ {stats.worst_session.location}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Day of Week Stats */}
                    {stats.day_of_week_stats && stats.day_of_week_stats.length > 0 && (
                        <div className="mb-4 sm:mb-6">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Performance by Day</div>
                            <div className="grid grid-cols-7 gap-1">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                                    const dayData = stats.day_of_week_stats.find(d => d.day.startsWith(day));
                                    return (
                                        <div key={day} className="text-center">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{day}</div>
                                            {dayData ? (
                                                <>
                                                    <div className={`text-xs font-bold ${dayData.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                        {dayData.profit >= 0 ? '+' : ''}{Math.round(dayData.profit)}
                                                    </div>
                                                    <div className="text-xs text-gray-400">{dayData.sessions}s</div>
                                                </>
                                            ) : (
                                                <div className="text-xs text-gray-400">-</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Location Stats */}
                    {stats.location_stats && stats.location_stats.length > 0 && (
                        <div className="mb-4 sm:mb-6">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Top Locations</div>
                            <div className="space-y-1">
                                {stats.location_stats.slice(0, 3).map((loc, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm">
                                        <span className="text-gray-700 dark:text-gray-300 truncate">{loc.location}</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-medium ${loc.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                {formatCurrency(loc.profit)}
                                            </span>
                                            <span className="text-xs text-gray-400">({loc.sessions}s)</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="h-64 sm:h-80 lg:h-96">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default StatisticsSummary; 